import { useContext, useMemo } from "react"
import { useIntradayQueryInvalidation } from "./use-intraday-query-invalidation"
import { useSelector } from "react-redux"
import moment from "moment"
import { omit, chain, split, keyBy, isEmpty, compact, get } from "lodash"
import { useInstrumentsWithQueryOptions, useBaseWithSegment, SELECTED_MODE_VALUES } from "connected-components/dashboard/helpers"
import { getToday } from "helpers/types/dates"
import { useModelMetadata } from "resources/models"
import { OK } from "helpers/data-statuses"
import { createCompositionContributorsQuery } from "gatherers/contributors/composition"
import { createContentSetContributorsQuery } from "gatherers/contributors/content-set"
import { createFactorContentSetQuery } from "gatherers/contributors/factor-content-set"
import { createRiskFactorsQuery } from "gatherers/contributors/factor-risk"
import { createPerformanceFactorsQuery } from "gatherers/contributors/factor-performance"
import { createBetaContributorsQuery } from "gatherers/contributors/beta"
import { createRiskContributorsQuery } from "gatherers/contributors/risk"
import { createExposureContributorsQuery } from "gatherers/contributors/exposure"
import { createPerformanceContributorsQuery } from "gatherers/contributors/performance"
import { createFactorBetaQuery } from "gatherers/contributors/factor-beta"
import { createFactorSensitivityQuery } from "gatherers/contributors/factor-sensitivity"
import { createFactorSensitivityContributorsQuery } from "gatherers/contributors/sensitivities"
import { createMarginalContributionQuery } from "gatherers/contributors/marginalContribution"
import { createForecastContributorsQuery } from "gatherers/contributors/forecast"
import { useIsOpPro, useIsRfrAttributionEnabled } from "resources/account"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import { getGatherersFromInstruments, getIntradayGatherers } from "../../asset-view/helpers"
import useSecuritySearchCount from "./use-security-search-count"
import { useIntradayState } from "./use-intraday-state"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"

const INTRADAY_CONTRIBUTION_PERIODS = [
  {
    metricIds: [
      "intraday.contribution1d",
      "intraday.contributionPercent1d",
      "intraday.factorsContribution1d",
      "intraday.factorsContributionPercent1d",
      "intraday.specificContribution1d",
      "intraday.specificContributionPercent1d",
    ],
    periodKeySuffix: "1d",
    period: "1d",
  },
  {
    metricIds: [
      "intraday.contribution1w",
      "intraday.contributionPercent1w",
      "intraday.factorsContribution1w",
      "intraday.factorsContributionPercent1w",
      "intraday.specificContribution1w",
      "intraday.specificContributionPercent1w",
    ],
    periodKeySuffix: "1w",
    period: "1w",
  },
  {
    metricIds: [
      "intraday.contribution1m",
      "intraday.contributionPercent1m",
      "intraday.factorsContribution1m",
      "intraday.factorsContributionPercent1m",
      "intraday.specificContribution1m",
      "intraday.specificContributionPercent1m",
    ],
    periodKeySuffix: "1m",
    period: "1m",
  },
]
function getContributionPeriodsToInclude(selectedMetrics = []) {
  const selectedMetricSet = new Set(selectedMetrics.map(String))
  return INTRADAY_CONTRIBUTION_PERIODS.filter(({ metricIds }) => metricIds.some(metricId => selectedMetricSet.has(metricId)))
}

export function getRelativeStartDate(endDate, period) {
  const end = moment(endDate)
  if (!end.isValid()) {
    return endDate
  }

  if (period === "MTD") {
    return end.startOf("month").format("YYYY-MM-DD")
  }

  if (period === "1d") {
    return endDate
  }

  if (period === "1w") {
    return end.clone().subtract(1, "weeks").format("YYYY-MM-DD")
  }

  if (period === "1m") {
    return end.clone().subtract(1, "months").format("YYYY-MM-DD")
  }

  return endDate
}

export function mapIntradayContributionValues(node, prefix) {
  if (!node || typeof node !== "object") {
    return node
  }

  const mappedNode = { ...node }
  const metricValues = get(node, ["values", prefix])
  if (metricValues && typeof metricValues === "object") {
    const descriptors = get(metricValues, "descriptors")
    mappedNode.values = {
      ...(node.values || {}),
      [prefix]: {
        contributionPercent: get(metricValues, "totalReturn.cumulativePercent", null),
        contributionCurrency: get(metricValues, "totalReturn.cumulativeCurrency", null),
        factorsContribution: get(metricValues, "factorsReturn.cumulativeCurrency", null),
        factorsContributionPercent: get(metricValues, "factorsReturn.cumulativePercent", null),
        specificContribution: get(metricValues, "specificReturn.cumulativeCurrency", null),
        specificContributionPercent: get(metricValues, "specificReturn.cumulativePercent", null),
        ...(descriptors ? { descriptors } : {}),
      },
    }
  }

  if (!node.children || typeof node.children !== "object") {
    return mappedNode
  }

  mappedNode.children = Object.entries(node.children).reduce((childrenAcc, [childId, childNode]) => {
    childrenAcc[childId] = mapIntradayContributionValues(childNode, prefix)
    return childrenAcc
  }, {})

  return mappedNode
}

export function createIntradayContributionQueryGatherers({
  selectedMetrics,
  instrumentsWithQueryOptions,
  modelId,
  todayDate,
  shouldAutoInvalidateOnPricingUpdates,
  groupBy,
  base,
  showNotHeld,
}) {
  const periodsToInclude = getContributionPeriodsToInclude(selectedMetrics)
  if (periodsToInclude.length === 0) {
    return []
  }

  return getGatherersFromInstruments(instrumentsWithQueryOptions, (instrument, keySuffix) => {
    return periodsToInclude.map(({ period, periodKeySuffix }) => {
      const prefix = `intradayContribution${periodKeySuffix}${keySuffix}`
      const gatherer = createPerformanceContributorsQuery({
        prefix,
        modelId,
        positionSet: instrument,
        from: getRelativeStartDate(todayDate, period),
        to: todayDate,
        groupByOptions: groupBy,
        base,
        notHeldRollup: !showNotHeld,
      })

      return {
        ...gatherer,
        shouldAutoInvalidateOnPricingUpdates,
        select: (result) => mapIntradayContributionValues(gatherer.select(result), prefix),
      }
    })
  })
}

const createCommonQueryOptions = ({ showNotHeld, selectedCategory, instrument, from, to, startDate, endDate, groupBy, contextBase, isEndVsStartComparisonInstrument, ...params }) => {
  const fromDate = from || startDate
  const toDate = to || endDate
  return {
    notHeldRollup: !showNotHeld,
    category: selectedCategory,
    positionSet: instrument,
    from: fromDate,
    to: isEndVsStartComparisonInstrument ? fromDate : toDate,
    groupByOptions: groupBy,
    base: contextBase,
    ...params,
  }
}

function useTemplateQueryFactories() {
  return useMemo(() => ({
    composition: (params) => [
      ...createCompositionContributorsQuery({
        prefix: `composition${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
      ...createCompositionContributorsQuery({
        prefix: `marketValue${params.keySuffix}`,
        scaleFormat: "DEFAULT",
        ...createCommonQueryOptions(params),
      }),
    ],

    contentSet: (params) => {
      const { contentSetId } = params
      return [
        createContentSetContributorsQuery({
          prefix: `${contentSetId}${params.keySuffix}`,
          ...createCommonQueryOptions(params),
          contentSetId,
        }),
      ]
    },

    factorContentSet: (params) => {
      const { contentSetId } = params
      return [
        createFactorContentSetQuery({
          prefix: `${contentSetId}${params.keySuffix}`,
          ...createCommonQueryOptions(params),
          contentSetId,
        }),
      ]
    },

    summary: (params) => [
      createPerformanceContributorsQuery({
        prefix: `performance${params.keySuffix}`,
        ...createCommonQueryOptions({ ...params, from: params.from || params.computedPerformanceStartDate }),
      }),
      ...(!params.isOpPro ? [
        createRiskContributorsQuery({
          prefix: `risk${params.keySuffix}`,
          ...createCommonQueryOptions(params),
        }),
      ] : []),
      ...(params.betaFields?.length > 0 ? [
        createBetaContributorsQuery({
          prefix: `beta${params.keySuffix}`,
          ...createCommonQueryOptions(params),
        }),
      ] : []),
    ],

    exposure: (params) => [
      createExposureContributorsQuery({
        prefix: `exposure${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    risk: (params) => [
      createRiskContributorsQuery({
        prefix: `risk${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    predictedFactorSensitivity: (params) => [
      createFactorSensitivityContributorsQuery({
        prefix: `predictedFactorSensitivity${params.keySuffix}`,
        factorSensitivityType: FACTOR_SENSITIVITY_TYPES.PREDICTED,
        ...createCommonQueryOptions(params),
      }),
    ],

    historicalFactorSensitivity: (params) => [
      createFactorSensitivityContributorsQuery({
        prefix: `historicalFactorSensitivity${params.keySuffix}`,
        factorSensitivityType: FACTOR_SENSITIVITY_TYPES.HISTORICAL,
        ...createCommonQueryOptions(params),
      }),
    ],

    factorRisk: (params) => {
      const { driftStartDate, isEndVsStartComparisonInstrument, from } = params
      const fromDate = isEndVsStartComparisonInstrument ? from : driftStartDate
      return [
        createRiskFactorsQuery({
          prefix: `risk${params.keySuffix}`,
          ...createCommonQueryOptions({ ...params, from: fromDate }),
        }),
        createFactorSensitivityQuery({
          prefix: `sensitivity${params.keySuffix}`,
          ...createCommonQueryOptions(params),
        }),
      ]
    },

    marginal: (params) => [
      createMarginalContributionQuery({
        prefix: `marginal${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    performance: (params) => [
      createPerformanceContributorsQuery({
        prefix: `performance${params.keySuffix}`,
        ...createCommonQueryOptions({ ...params, from: params.from || params.computedPerformanceStartDate }),
      }),
    ],

    factorPerformance: (params) => [
      createPerformanceFactorsQuery({
        prefix: `performance${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    beta: (params) => [
      createBetaContributorsQuery({
        prefix: `beta${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    factorBeta: (params) => [
      createFactorBetaQuery({
        prefix: `beta${params.keySuffix}`,
        ...createCommonQueryOptions(params),
      }),
    ],

    forecast: (params) => {
      const { forecastId, forecastSecurities } = params
      const isImpliedReturns = forecastId === "impliedReturns"
      let additionalParams = { forecastSecurities: null }
      if (isImpliedReturns && isEmpty(forecastSecurities)) {
        return null
      } else if (isImpliedReturns) {
        additionalParams = { forecastId: null, forecastSecurities }
      }
      return [
        createForecastContributorsQuery({
          prefix: `${forecastId}${params.keySuffix}`,
          ...createCommonQueryOptions(params),
          ...additionalParams,
        }),
      ]
    },
  }), [])
}

function getContentSetGatherer(contentSetId, options, templateQueryFactories) {
  return templateQueryFactories.contentSet({
    ...options,
    contentSetId,
    selectedCategory: null,
    selectedMetricDisplay: null,
  })
}

function getForecastGatherer(forecastId, options, templateQueryFactories) {
  return templateQueryFactories.forecast({
    ...options,
    forecastId,
  })
}

function getTemplateFactoryForKey({ key, contentSetKeys, forecastKeys, templateQueryFactories }) {
  if (contentSetKeys[key]) {
    return (options) => getContentSetGatherer(key, options, templateQueryFactories)
  }

  if (forecastKeys[key] || key === "impliedReturns") {
    return (options) => getForecastGatherer(key, options, templateQueryFactories)
  }

  return templateQueryFactories[key] || null
}

function computeMetricKeys(selectedMetrics, classifications) {
  const classificationKeys = keyBy(classifications, "id")
  const groupedKeys = chain(selectedMetrics)
    .groupBy((id) => split(id, ".")[0])
    .keys()
    .value()
  return { classificationKeys, groupedKeys }
}

function makeCustomGatherers({ contentSets, forecasts, ...options }, templateQueryFactories) {
  const contentSetKeys = keyBy(contentSets, "id")
  const forecastKeys = keyBy(forecasts, "id")

  const otherQueries = options.keys.flatMap((key) => {
    const templateFactory = getTemplateFactoryForKey({ key, contentSetKeys, forecastKeys, templateQueryFactories })
    return templateFactory ? templateFactory(options) : []
  })

  return otherQueries
}

function getTemplateGatherersOptions(templateOptions, templateType) {
  const isTradeQuery = templateType === "trade"
  const useCompositionQueries = templateOptions.useCompositionQueries ?? true
  const options = omit(templateOptions, "useCompositionQueries")

  return { useCompositionQueries, isTradeQuery, options, templateType }
}

function useTemplateGatherersData(contextData) {
  const { modelId, endDate, startDate, base: contextBase, groupBy, showNotHeld, instrument: original, secondInstrument: rebalanced, comparisonInstrument: comparison, target, selectedMode, isExpanded, normalization, filter, modelMetadata } = contextData

  const instrumentOptions = useMemo(() => ({ isExpanded, normalization, filter }), [isExpanded, normalization, filter])
  const instrumentsWithQueryOptions = useInstrumentsWithQueryOptions({ original, rebalanced, comparison, target }, selectedMode, instrumentOptions)
  const baseWithSegment = useBaseWithSegment(contextBase, filter, normalization)

  const defaultProps = useMemo(() => ({ modelId, contextBase: baseWithSegment, endDate, startDate, groupBy, showNotHeld, modelName: modelMetadata?.name }), [modelId, baseWithSegment, endDate, startDate, groupBy, showNotHeld, modelMetadata?.name])

  return { instrumentsWithQueryOptions, defaultProps }
}

function instrumentIsPortfolio(instrument) {
  return instrument.type === "PORTFOLIO" || instrument.rulesConfiguration?.source?.positionSet?.type === "PORTFOLIO"
}

function createGathererQueries(instrument, keySuffix, templateType, defaultProps, options, useCompositionQueries, templateQueryFactories, isEndVsStartComparisonInstrument) {
  const queries = []

  const { classificationKeys, groupedKeys } = computeMetricKeys(options.selectedMetrics, options?.classifications)

  let needsDescriptors = false
  let needsAttributes = false
  if (templateType === "custom" && options.selectedMetrics) {
    needsDescriptors = groupedKeys.includes("descriptors") || groupedKeys.some(key => classificationKeys[key])
    needsAttributes = groupedKeys.includes("attributes") && instrumentIsPortfolio(instrument)
  }

  if (useCompositionQueries) {
    queries.push(...templateQueryFactories.composition({
      keySuffix,
      instrument,
      isEndVsStartComparisonInstrument,
      ...defaultProps,
      ...options,
      ...(needsDescriptors ? { useDescriptors: true } : {}),
      ...(needsAttributes ? { useAttributes: true } : {}),
    }))
  }

  const keys = groupedKeys.filter(key => !["descriptors", "attributes"].includes(key) && !classificationKeys[key])

  const templateFactory = templateType === "custom" ? (params) => makeCustomGatherers({ ...params, keys }, templateQueryFactories) : templateQueryFactories[templateType]
  if (templateFactory) {
    queries.push(...templateFactory({ keySuffix, instrument, isEndVsStartComparisonInstrument, ...defaultProps, ...options }))
  }

  return compact(queries)
}

export function applyIntradayQueryOptions({
  instrumentOptions,
  isIntradayMode,
  instrumentType,
  endDate,
  startDate,
  latestDate,
  isEndVsStartComparisonInstrument,
  intradayBatchMetadata,
  instrumentsMetadata,
}) {
  const nextInstrumentOptions = { ...instrumentOptions, shouldUseIntradayQuery: false }
  if (!isIntradayMode) {
    return nextInstrumentOptions
  }

  if (instrumentType !== "comparison") {
    const isInstrumentIntradayEnabled = instrumentsMetadata?.[instrumentType]?.intradayEnabled
    if (isInstrumentIntradayEnabled) {
      nextInstrumentOptions.on = endDate
      nextInstrumentOptions.shouldUseIntradayQuery = true
      if (instrumentType === "original" || instrumentType === "rebalanced") {
        nextInstrumentOptions.batchId = intradayBatchMetadata?.id
      }
    }
    return nextInstrumentOptions
  }

  // we make an exception for end vs start comparison instrument
  // if we're in end vs start comparison
  // if we're in intraday mode
  // if start date is equal to end date, which is today due to intraday mode
  // then we can use the intraday query
  if (isEndVsStartComparisonInstrument && startDate === endDate) {
    const isComparisonIntradayEnabled = instrumentsMetadata?.comparison?.intradayEnabled
    if (isComparisonIntradayEnabled) {
      nextInstrumentOptions.on = startDate
      nextInstrumentOptions.shouldUseIntradayQuery = true
    }
  }
  if (endDate > latestDate) {
    nextInstrumentOptions.to = latestDate
  }
  return nextInstrumentOptions
}

export function useTemplateGatherers(context, templateType, templateOptions = {}) {
  const contextData = useContext(context)
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(contextData.modelId)
  const isOpPro = useIsOpPro()
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()
  const { useCompositionQueries, options } = useMemo(() => getTemplateGatherersOptions(templateOptions, templateType), [templateOptions, templateType])

  // For trade template, always get both original and rebalanced data to display original data alongside rebalanced data in add trade mode
  const effectiveSelectedMode = templateType === "trade"
    ? SELECTED_MODE_VALUES.rebalancedVsOriginal
    : contextData.selectedMode

  const { instrumentsWithQueryOptions, defaultProps } = useTemplateGatherersData({ ...contextData, modelMetadata, selectedMode: effectiveSelectedMode })
  const templateQueryFactories = useTemplateQueryFactories()
  const {
    intradayBatchMetadata,
    latestDate,
    endDate,
    isEndVsStartDateComparison,
    startDate,
    modelId,
    instrumentsMetadata,
  } = contextData
  const [{ isIntradayMode }] = useIntradayState()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  const todayDate = getToday()

  const isCustomTemplate = templateType == "custom"
  const comparisonCount = useSecuritySearchCount(createCommonQueryOptions({ ...contextData, instrument: instrumentsWithQueryOptions.comparison, enabled: isCustomTemplate }))
  const rebalancedCount = useSecuritySearchCount(createCommonQueryOptions({ ...contextData, instrument: instrumentsWithQueryOptions.rebalanced, enabled: isCustomTemplate }))
  const originalCount = useSecuritySearchCount(createCommonQueryOptions({ ...contextData, enabled: isCustomTemplate }))

  const counts = useMemo(() => ({
    comparison: comparisonCount,
    rebalanced: rebalancedCount,
    original: originalCount,
  }), [comparisonCount, rebalancedCount, originalCount])

  const fullOptions = useMemo(() => ({ ...options, isOpPro, isRfrAttributionEnabled }), [options, isOpPro, isRfrAttributionEnabled])

  const intradayBatchId = intradayBatchMetadata?.id

  const gatherers = useMemo(() => {
    if (modelMetadataStatus === OK) {
      const gatherers = getGatherersFromInstruments(instrumentsWithQueryOptions, (instrument, keySuffix, instrumentType) => {
        const instrumentOptions = { ...fullOptions, count: counts[instrumentType] }
        const isEndVsStartComparisonInstrument = isEndVsStartDateComparison && instrumentType === "comparison"
        const intradayAdjustedInstrumentOptions = applyIntradayQueryOptions({
          instrumentOptions,
          isIntradayMode,
          instrumentType,
          endDate,
          startDate,
          latestDate,
          isEndVsStartComparisonInstrument,
          intradayBatchMetadata: { id: intradayBatchId },
          instrumentsMetadata,
        })

        return createGathererQueries(instrument, keySuffix, templateType, defaultProps, intradayAdjustedInstrumentOptions, useCompositionQueries, templateQueryFactories, isEndVsStartComparisonInstrument)
      })

      const shouldIncludeIntradayMetrics = isIntradayAnalyticsEnabled
        && !!modelId
        && fullOptions.selectedMetrics?.some((id) => String(id).startsWith("intraday."))

      if (shouldIncludeIntradayMetrics) {
        gatherers.push(...getIntradayGatherers(instrumentsWithQueryOptions, modelId, fullOptions.selectedMetrics))
        gatherers.push(...createIntradayContributionQueryGatherers({
          selectedMetrics: fullOptions.selectedMetrics,
          instrumentsWithQueryOptions,
          modelId,
          todayDate,
          shouldAutoInvalidateOnPricingUpdates: true,
          groupBy: defaultProps.groupBy,
          base: defaultProps.contextBase,
          showNotHeld: defaultProps.showNotHeld,
        }))
      }

      return gatherers
    }

    return []
  }, [
    instrumentsWithQueryOptions,
    templateType,
    defaultProps,
    useCompositionQueries,
    fullOptions,
    templateQueryFactories,
    modelMetadataStatus,
    intradayBatchId,
    latestDate,
    endDate,
    startDate,
    isEndVsStartDateComparison,
    isIntradayMode,
    counts,
    modelId,
    isIntradayAnalyticsEnabled,
    todayDate,
    instrumentsMetadata,
  ])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate)

  return gatherers
}
